import { Pressable, View } from 'react-native';
import { Button, Icon, Surface, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { formatLimitHours } from '../../../lib/drivingLimit';
import { levelTone } from './colors';
import { LimitBar } from './LimitBar';
import {
  drivingLimitNoticeMessage,
  formatHoursDecimal,
  formatShortDuration,
  limitLevels,
  mostUrgentLimit,
  progressRatio,
} from './limits';
import type { LimitKey, LimitLevel, DriverLimits } from './types';

const LIMIT_ICONS: Record<LimitKey, string> = {
  rolling24h: 'steering',
  week: 'calendar-week',
  fourMonth: 'chart-line',
};

const LEVEL_ICONS: Record<LimitLevel, string> = {
  ok: 'check-circle',
  warning: 'alert',
  critical: 'alert',
  reached: 'alert-octagon',
  unknown: 'information-outline',
};

interface RowModel {
  key: LimitKey;
  level: LimitLevel;
  ratio: number;
  value: string;
  detail: string | null;
}

const useRows = (limits: DriverLimits, nowMs?: number): RowModel[] => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language;
  const levels = limitLevels(limits, nowMs);
  const rows: RowModel[] = [];

  const d = limits.rolling24h;
  rows.push(
    d && levels.rolling24h !== 'unknown'
      ? {
          key: 'rolling24h',
          level: levels.rolling24h,
          ratio: progressRatio(d.driven_seconds, d.limit_seconds),
          value: t('workTime.limits.usedOf', {
            used: formatShortDuration(d.driven_seconds),
            limit: formatShortDuration(d.limit_seconds),
          }),
          detail:
            drivingLimitNoticeMessage(d, t, lang, nowMs) ??
            t('workTime.limits.remaining', { remaining: formatShortDuration(d.remaining_seconds) }),
        }
      : { key: 'rolling24h', level: 'unknown', ratio: 0, value: t('workTime.limits.noData'), detail: null },
  );

  const w = limits.week;
  rows.push(
    w
      ? {
          key: 'week',
          level: levels.week,
          ratio: progressRatio(w.driven_seconds, w.limit_seconds),
          value: t('workTime.limits.usedOf', {
            used: formatShortDuration(w.driven_seconds),
            limit: formatShortDuration(w.limit_seconds),
          }),
          detail:
            levels.week === 'reached'
              ? t('workTime.limits.week.reached')
              : t('workTime.limits.remaining', {
                  remaining: formatShortDuration(Math.max(0, w.limit_seconds - w.driven_seconds)),
                }),
        }
      : { key: 'week', level: 'unknown', ratio: 0, value: t('workTime.limits.noData'), detail: null },
  );

  const a = limits.fourMonth;
  if (a) {
    const limit = formatLimitHours(a.limit_weekly_seconds, lang);
    const over =
      a.average_weekly_seconds > a.limit_weekly_seconds ||
      a.allowed_this_week_seconds <= (limits.week?.driven_seconds ?? 0);
    // Never suggest more than the weekly limit itself.
    const weeklyCap = limits.week?.limit_seconds ?? 60 * 3600;
    const available = Math.min(a.allowed_this_week_seconds, weeklyCap);
    // Rolling 17 weeks: the «até X h esta semana» hint only helps when it is
    // below the weekly limit; otherwise the row says what the average covers.
    const detail = over
      ? t('workTime.limits.fourMonth.over', { limit })
      : available < weeklyCap
        ? t('workTime.limits.fourMonth.available', { limit, hours: formatHoursDecimal(available, lang) })
        : t('workTime.limits.fourMonth.window', { weeks: a.window_weeks });
    rows.push({
      key: 'fourMonth',
      level: levels.fourMonth,
      ratio: progressRatio(a.average_weekly_seconds, a.limit_weekly_seconds),
      value: t('workTime.limits.fourMonth.value', {
        avg: formatHoursDecimal(a.average_weekly_seconds, lang),
        limit,
      }),
      detail: a.has_full_history ? detail : `${detail} ${t('workTime.limits.fourMonth.partial')}`,
    });
  } else {
    rows.push({ key: 'fourMonth', level: 'unknown', ratio: 0, value: t('workTime.limits.noData'), detail: null });
  }
  return rows;
};

const levelLabelKey = (key: LimitKey, level: LimitLevel) =>
  // The 4-month limit is an average over a rolling 17-week window, so an
  // average above 48 h is a warning to act on, not a limit already broken.
  key === 'fourMonth' && level === 'reached' ? 'workTime.limits.fourMonth.above' : `workTime.limits.levels.${level}`;

const LevelBadge = ({ level, limitKey }: { level: LimitLevel; limitKey: LimitKey }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = levelTone(level, theme);
  const ok = level === 'ok' || level === 'unknown';
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 999,
        backgroundColor: ok ? 'transparent' : tone.container,
      }}
    >
      <Icon source={LEVEL_ICONS[level]} size={14} color={ok ? theme.colors.onSurfaceVariant : tone.onContainer} />
      <Text variant="labelSmall" style={{ color: ok ? theme.colors.onSurfaceVariant : tone.onContainer }}>
        {t(levelLabelKey(limitKey, level))}
      </Text>
    </View>
  );
};

const LimitRow = ({ row, highlighted }: { row: RowModel; highlighted: boolean }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tone = levelTone(row.level, theme);
  const barColor = row.level === 'ok' || row.level === 'unknown' ? theme.colors.primary : tone.accent;
  return (
    <View
      testID={`limit-row-${row.key}`}
      style={{
        gap: 6,
        padding: 12,
        borderRadius: 12,
        borderWidth: highlighted ? 2 : 1,
        borderColor: highlighted ? tone.accent : theme.colors.outlineVariant,
        backgroundColor: highlighted ? tone.container : 'transparent',
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Icon source={LIMIT_ICONS[row.key]} size={20} color={highlighted ? tone.onContainer : theme.colors.onSurface} />
        <Text
          variant="titleSmall"
          style={{ flex: 1, color: highlighted ? tone.onContainer : theme.colors.onSurface, fontWeight: '600' }}
        >
          {t(`workTime.limits.${row.key}.title`)}
        </Text>
        <LevelBadge level={row.level} limitKey={row.key} />
      </View>
      <Text
        variant="bodyLarge"
        style={{ color: highlighted ? tone.onContainer : theme.colors.onSurface, fontVariant: ['tabular-nums'] }}
      >
        {row.value}
      </Text>
      <LimitBar
        ratio={row.ratio}
        color={barColor}
        accessibilityLabel={`${t(`workTime.limits.${row.key}.title`)}: ${row.value}`}
        testID={`limit-bar-${row.key}`}
      />
      {row.detail ? (
        <Text
          variant="bodyMedium"
          accessibilityRole={row.level === 'ok' || row.level === 'unknown' ? undefined : 'alert'}
          style={{ color: highlighted ? tone.onContainer : theme.colors.onSurfaceVariant }}
        >
          {row.detail}
        </Text>
      ) : null}
    </View>
  );
};

/**
 * Full limits card, driving time only: 10 h in a rolling 24 h, 60 h this week
 * and the 4-month weekly average of 48 h. The most urgent limit is highlighted.
 */
export const DriverLimitsCard = ({
  limits,
  nowMs,
  onClose,
}: {
  limits: DriverLimits;
  nowMs?: number;
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const rows = useRows(limits, nowMs);
  const urgent = mostUrgentLimit(limitLevels(limits, nowMs));

  return (
    <Surface
      testID="driver-limits-card"
      elevation={1}
      style={{
        padding: 16,
        borderRadius: 16,
        gap: 12,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.outlineVariant,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Icon source="timer-sand" size={24} color={theme.colors.primary} />
        <View style={{ flex: 1 }}>
          <Text variant="titleLarge" style={{ color: theme.colors.onSurface }}>
            {t('workTime.limits.title')}
          </Text>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
            {t('workTime.limits.subtitle')}
          </Text>
        </View>
        {onClose ? (
          <Button onPress={onClose} compact contentStyle={{ minHeight: 44 }}>
            {t('workTime.limits.close')}
          </Button>
        ) : null}
      </View>

      {[...rows]
        .sort((a, b) => (a.key === urgent ? -1 : b.key === urgent ? 1 : 0))
        .map((row) => (
          <LimitRow key={row.key} row={row} highlighted={row.key === urgent} />
        ))}

      <View
        style={{ gap: 4, padding: 12, borderRadius: 12, backgroundColor: theme.colors.surfaceVariant }}
        testID="limits-how-we-count"
      >
        <Text variant="titleSmall" style={{ color: theme.colors.onSurfaceVariant }}>
          {t('workTime.limits.howWeCount.title')}
        </Text>
        {(['driving', 'periods', 'notCounted', 'disclaimer'] as const).map((k) => (
          <Text key={k} variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
            {t(`workTime.limits.howWeCount.${k}`)}
          </Text>
        ))}
      </View>
    </Surface>
  );
};

/**
 * Compact strip for the home status card: one mini bar per limit and, when a
 * limit needs attention, its message. For the 24 h limit the message is the
 * existing 10 h notice text, shown under the same conditions as before.
 */
export const DriverLimitsSummary = ({
  limits,
  nowMs,
  onOpen,
}: {
  limits: DriverLimits;
  nowMs?: number;
  onOpen?: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language;
  const theme = useTheme();
  const rows = useRows(limits, nowMs);
  const urgent = mostUrgentLimit(limitLevels(limits, nowMs));
  // One line per limit that needs attention, most severe first. The 24 h line
  // is the existing 10 h notice (ALL-41) and shows exactly when it shows today,
  // even if another limit is more urgent.
  const alerts = rows
    .filter((r) =>
      r.key === 'rolling24h'
        ? drivingLimitNoticeMessage(limits.rolling24h, t, lang, nowMs) !== null
        : r.level === 'warning' || r.level === 'critical' || r.level === 'reached',
    )
    .sort((a, b) => (a.key === urgent ? -1 : b.key === urgent ? 1 : 0))
    .map((r) => ({
      row: r,
      tone: levelTone(r.level, theme),
      text:
        r.key === 'rolling24h'
          ? (drivingLimitNoticeMessage(limits.rolling24h, t, lang, nowMs) as string)
          : r.key === 'week'
            ? t('workTime.limits.weekWarning', {
                used: formatShortDuration(limits.week?.driven_seconds ?? 0),
                limit: formatShortDuration(limits.week?.limit_seconds ?? 0),
              })
            : t('workTime.limits.fourMonthWarning', {
                avg: formatHoursDecimal(limits.fourMonth?.average_weekly_seconds ?? 0, lang),
                limit: formatHoursDecimal(limits.fourMonth?.limit_weekly_seconds ?? 0, lang),
              }),
    }));

  // Hours instead of %, so the driver reads the limits without maths
  // (Ronald, 2026-10-30): one row per limit, label · bar · used / limit.
  const summaryValue = (key: LimitKey): string | null => {
    if (key === 'rolling24h' && limits.rolling24h) {
      return t('workTime.limits.summaryValue', {
        used: formatShortDuration(limits.rolling24h.driven_seconds),
        limit: formatShortDuration(limits.rolling24h.limit_seconds),
      });
    }
    if (key === 'week' && limits.week) {
      return t('workTime.limits.summaryValue', {
        used: formatShortDuration(limits.week.driven_seconds),
        limit: formatShortDuration(limits.week.limit_seconds),
      });
    }
    if (key === 'fourMonth' && limits.fourMonth) {
      return t('workTime.limits.fourMonth.summaryValue', {
        avg: formatHoursDecimal(limits.fourMonth.average_weekly_seconds, lang),
        limit: formatHoursDecimal(limits.fourMonth.limit_weekly_seconds, lang),
      });
    }
    return null;
  };

  const content = (
    <View style={{ gap: 8 }}>
      <View style={{ gap: 6 }}>
        {rows.map((row) => {
          const tone = levelTone(row.level, theme);
          const ok = row.level === 'ok' || row.level === 'unknown';
          const value = row.level === 'unknown' ? null : summaryValue(row.key);
          return (
            <View
              key={row.key}
              testID={`limit-mini-${row.key}`}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 22 }}
            >
              <Text variant="labelMedium" style={{ width: 56, color: theme.colors.onSurfaceVariant }}>
                {t(`workTime.limits.${row.key}.short`)}
              </Text>
              <View style={{ flex: 1 }}>
                <LimitBar ratio={row.ratio} color={ok ? theme.colors.primary : tone.accent} height={6} />
              </View>
              <Text
                variant="labelMedium"
                style={{
                  minWidth: 104,
                  textAlign: 'right',
                  color: ok ? theme.colors.onSurface : tone.accent,
                  fontWeight: '700',
                  fontVariant: ['tabular-nums'],
                }}
              >
                {value ?? t('workTime.limits.levels.unknown')}
              </Text>
            </View>
          );
        })}
      </View>
      {alerts.map(({ row, tone, text }) => (
        <View
          key={row.key}
          testID={row.key === 'rolling24h' ? 'driving-limit-notice' : `limits-summary-alert-${row.key}`}
          accessibilityRole="alert"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            padding: 10,
            borderRadius: 10,
            backgroundColor: tone.container,
          }}
        >
          <Icon source={LEVEL_ICONS[row.level]} size={20} color={tone.onContainer} />
          <Text variant="bodyMedium" style={{ flex: 1, color: tone.onContainer }}>
            {text}
          </Text>
        </View>
      ))}
    </View>
  );

  if (!onOpen) return <View testID="driver-limits-summary">{content}</View>;
  return (
    <Pressable
      testID="driver-limits-summary"
      accessibilityRole="button"
      accessibilityLabel={t('workTime.limits.viewAll')}
      onPress={onOpen}
      style={({ pressed }) => ({
        minHeight: 44,
        padding: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.outlineVariant,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Text variant="labelLarge" style={{ flex: 1, color: theme.colors.onSurface }}>
          {t('workTime.limits.title')}
        </Text>
        <Text variant="labelLarge" style={{ color: theme.colors.primary }}>
          {t('workTime.limits.viewAll')}
        </Text>
        <Icon source="chevron-right" size={18} color={theme.colors.primary} />
      </View>
      {content}
    </Pressable>
  );
};
